import type { FarmCurrentWeather } from '../utils/farmCurrentWeather';
import {
  formatTempC,
  formatWindMs,
  weatherMood,
  type WeatherMood,
} from '../utils/farmCurrentWeather';

type Props = {
  weather?: FarmCurrentWeather | null;
  isDark?: boolean;
};

const MOOD_META: Record<
  WeatherMood,
  { label: string; icon: string }
> = {
  clear: { label: 'Clear', icon: 'ri-sun-line' },
  cloudy: { label: 'Cloudy', icon: 'ri-cloudy-line' },
  rain: { label: 'Rain', icon: 'ri-rainy-line' },
  heat: { label: 'Heat', icon: 'ri-temp-hot-line' },
  snow: { label: 'Snow', icon: 'ri-snowy-line' },
  storm: { label: 'Storm', icon: 'ri-thunderstorms-line' },
};

/**
 * Compact sky-tint strip for Saved Field cards: mood + temp + wind.
 * Not a full weather widget — one thin bar only.
 */
export default function WeatherMoodStrip({ weather, isDark = true }: Props) {
  const mood = weatherMood(weather);
  if (!weather || !mood) return null;

  const meta = MOOD_META[mood];
  const temp = formatTempC(weather.temperature_c);
  const wind = formatWindMs(weather.wind_speed_ms);
  const title = [weather.description, temp, wind].filter(Boolean).join(' · ');

  return (
    <div
      className={`weather-mood-strip weather-mood-strip--${mood} ${
        isDark ? 'weather-mood-strip--dark' : 'weather-mood-strip--light'
      }`}
      title={title || meta.label}
      aria-label={`Weather ${meta.label}${temp ? `, ${temp}` : ''}${wind ? `, wind ${wind}` : ''}`}
    >
      <div className="weather-mood-strip__glow" aria-hidden />
      <div className="weather-mood-strip__row">
        <span className="weather-mood-strip__mood">
          <i className={meta.icon} aria-hidden />
          <span>{meta.label}</span>
        </span>
        <span className="weather-mood-strip__stats">
          {temp && (
            <span className="weather-mood-strip__stat">
              <i className="ri-temp-line" aria-hidden />
              {temp}
            </span>
          )}
          {wind && (
            <span className="weather-mood-strip__stat">
              <i className="ri-windy-line" aria-hidden />
              {wind}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
